import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import {
  ChevronLeft,
  Edit,
  Trash2,
  Copy,
  Clock,
  Send,
  CheckCircle,
  XCircle,
  Calendar,
  MessageSquare,
  BarChart3,
  Loader2,
  User,
  AlertTriangle,
  ArrowRight,
  History,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Eye,
  Heart,
  MousePointer,
  Share2,
  ThumbsUp,
  TrendingUp,
  TrendingDown,
  Image,
  Video,
  Play,
  X,
  ZoomIn,
  Monitor
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import DashboardLayout from '../components/DashboardLayout';
import StatusProgressBar from '../components/StatusProgressBar';
import CommentsSection from '../components/postdetail/CommentsSection';
import EngagementMetrics from '../components/postdetail/EngagementMetrics';
import ActivityLog from '../components/postdetail/ActivityLog';
import {
  FacebookPreview,
  InstagramPreview,
  TwitterPreview,
  LinkedInPreview,
  TikTokPreview,
  YouTubePreview,
  PinterestPreview,
  GenericPreview,
} from '../components/postdetail/PlatformPreviews';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';

const PLATFORM_INFO = {
  facebook: { name: 'Facebook', icon: Facebook, color: '#1877F2' },
  instagram: { name: 'Instagram', icon: Instagram, color: '#E4405F' },
  twitter: { name: 'Twitter/X', icon: Twitter, color: '#1DA1F2' },
  linkedin: { name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  youtube: { name: 'YouTube', icon: Youtube, color: '#FF0000' },
  tiktok: { name: 'TikTok', emoji: '🎵', color: '#000000' },
  pinterest: { name: 'Pinterest', emoji: '📌', color: '#E60023' },
  threads: { name: 'Threads', emoji: '🧵', color: '#000000' },
  bluesky: { name: 'Bluesky', emoji: '🦋', color: '#0085FF' },
  snapchat: { name: 'Snapchat', emoji: '👻', color: '#FFFC00' },
};

const STATUS_CONFIG = {
  draft: { 
    label: 'Draft', 
    bg: 'bg-slate-100', 
    text: 'text-slate-700',
    description: 'This post is a draft and needs to be submitted for approval.',
    nextAction: 'Submit for Approval',
    nextStatus: 'pending_approval'
  },
  pending_approval: { 
    label: 'Pending Approval', 
    bg: 'bg-amber-100', 
    text: 'text-amber-700',
    description: 'This post is awaiting approval from a team member.',
    nextAction: 'Approve',
    nextStatus: 'approved'
  },
  approved: { 
    label: 'Approved', 
    bg: 'bg-blue-100', 
    text: 'text-blue-700',
    description: 'This post has been approved and is ready to be published.',
    nextAction: 'Publish Now',
    nextStatus: 'published'
  },
  scheduled: { 
    label: 'Scheduled', 
    bg: 'bg-emerald-100', 
    text: 'text-emerald-700',
    description: 'This post is scheduled and will be published automatically.',
    nextAction: 'Publish Now',
    nextStatus: 'published'
  },
  published: { 
    label: 'Published', 
    bg: 'bg-green-100', 
    text: 'text-green-700',
    description: 'This post has been published to all selected platforms.',
    nextAction: null,
    nextStatus: null
  },
  failed: { 
    label: 'Failed', 
    bg: 'bg-red-100', 
    text: 'text-red-700',
    description: 'Publishing failed. Please try again.',
    nextAction: 'Retry Publish',
    nextStatus: 'published'
  }
};

const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [postAnalytics, setPostAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const videoRef = useRef(null);

  const fetchPostData = useCallback(async () => {
    try {
      const [postRes, commentsRes] = await Promise.all([
        api.get(`/posts/${postId}`),
        api.get(`/posts/${postId}/comments`)
      ]);
      
      setPost(postRes.data);
      setComments(commentsRes.data);
      
      // Fetch calendar details
      const calendarRes = await api.get(`/calendars/${postRes.data.calendar_id}`);
      setCalendar(calendarRes.data);
    } catch (error) {
      toast.error('Failed to load post');
      navigate('/posts');
    } finally {
      setLoading(false);
    }
  }, [postId, navigate]);

  const fetchPostAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get(`/analytics/posts/${postId}/detailed`);
      setPostAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchPostData();
  }, [fetchPostData]);

  // Fetch analytics when post is published
  useEffect(() => {
    if (post?.status === 'published') {
      fetchPostAnalytics();
    }
  }, [post?.status, fetchPostAnalytics]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const handleSubmitForApproval = async () => {
    setActionLoading(true);
    try {
      await api.post(`/posts/${postId}/submit-approval`);
      await fetchPostData();
      toast.success('Post submitted for approval');
    } catch (error) {
      toast.error('Failed to submit for approval');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const response = await api.post(`/posts/${postId}/approve`);
      await fetchPostData();
      toast.success(`Post approved! Status: ${response.data.status}`);
    } catch (error) {
      toast.error('Failed to approve post');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await api.post(`/posts/${postId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      await fetchPostData();
      setRejectDialogOpen(false);
      setRejectReason('');
      toast.success('Post rejected and returned to draft');
    } catch (error) {
      toast.error('Failed to reject post');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = async () => {
    setActionLoading(true);
    try {
      const response = await api.post(`/posts/${postId}/publish`);
      await fetchPostData();
      
      const platformData = response.data.platform_data;
      const successCount = Object.values(platformData || {}).filter(p => p.published).length;
      const failCount = Object.values(platformData || {}).filter(p => !p.published).length;
      
      if (failCount === 0) {
        toast.success('Post published successfully to all platforms!');
      } else if (successCount > 0) {
        toast.warning(`Published to ${successCount} platform(s). ${failCount} platform(s) failed.`);
      } else {
        toast.error('Publishing failed on all platforms. Please check your connections.');
      }
    } catch (error) {
      toast.error('Failed to publish post');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await api.post(`/posts/${postId}/duplicate`);
      toast.success('Post duplicated');
      navigate(`/posts/${response.data.post_id}`);
    } catch (error) {
      toast.error('Failed to duplicate post');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/posts/${postId}`);
      toast.success('Post deleted');
      navigate('/posts');
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      const response = await api.post(`/posts/${postId}/comments`, {
        post_id: postId,
        content: newComment
      });
      setComments([...comments, response.data]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getNextActionButton = () => {
    const config = STATUS_CONFIG[post.status];
    if (!config?.nextAction) return null;

    const actionHandlers = {
      draft: handleSubmitForApproval,
      pending_approval: handleApprove,
      approved: handlePublish,
      scheduled: handlePublish,
      failed: handlePublish,
    };

    return (
      <Button
        onClick={actionHandlers[post.status]}
        disabled={actionLoading}
        className="bg-emerald-500 hover:bg-emerald-600 text-white"
        data-testid="next-action-btn"
      >
        {actionLoading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <ArrowRight className="h-4 w-4 mr-2" />
        )}
        {config.nextAction}
      </Button>
    );
  };

  // Helper functions for media
  const isVideoUrl = (url) => {
    return url?.includes('/video/') || url?.includes('.mp4') || url?.includes('.mov') || url?.includes('.webm');
  };

  const isImageUrl = (url) => {
    return url?.includes('/images/') || url?.includes('.png') || url?.includes('.jpg') || url?.includes('.jpeg') || url?.includes('.gif') || url?.includes('.webp');
  };

  const getVideoId = (url) => {
    if (!url) return null;
    const parts = url.split('/');
    return parts[parts.length - 1]?.replace('.mp4', '').replace('.mov', '').replace('.webm', '');
  };

  const getVideoThumbnailUrl = (url) => {
    const videoId = getVideoId(url);
    if (!videoId) return null;
    return `${process.env.REACT_APP_BACKEND_URL}/api/video/thumbnail/${videoId}?token=${localStorage.getItem('token')}`;
  };

  const openMediaModal = (url) => {
    setSelectedMedia(url);
    setMediaModalOpen(true);
  };

  const closeMediaModal = () => {
    // Stop video playback if playing
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setMediaModalOpen(false);
    setSelectedMedia(null);
  };

  // Render media preview for platform previews
  const renderMediaPreview = (aspectRatio = 'aspect-video', maxItems = 4) => {
    const mediaUrls = post?.media_urls || [];
    if (mediaUrls.length === 0) return null;

    const displayMedia = mediaUrls.slice(0, maxItems);
    
    if (displayMedia.length === 1) {
      const url = displayMedia[0];
      const isVideo = isVideoUrl(url);
      
      return (
        <div className={`${aspectRatio} bg-slate-100 relative overflow-hidden`}>
          {isVideo ? (
            <>
              <img
                src={getVideoThumbnailUrl(url)}
                alt="Video thumbnail"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="h-6 w-6 text-emerald-600 ml-1" />
                </div>
              </div>
            </>
          ) : (
            <img src={url} alt="Media" className="w-full h-full object-cover" />
          )}
        </div>
      );
    }

    // Grid layout for multiple items
    return (
      <div className={`grid ${displayMedia.length === 2 ? 'grid-cols-2' : 'grid-cols-2'} gap-0.5`}>
        {displayMedia.map((url, idx) => {
          const isVideo = isVideoUrl(url);
          return (
            <div key={idx} className={`${aspectRatio} bg-slate-100 relative overflow-hidden`}>
              {isVideo ? (
                <>
                  <img
                    src={getVideoThumbnailUrl(url)}
                    alt={`Video ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Play className="h-6 w-6 text-white" />
                  </div>
                </>
              ) : (
                <img src={url} alt={`Media ${idx + 1}`} className="w-full h-full object-cover" />
              )}
              {idx === maxItems - 1 && mediaUrls.length > maxItems && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">+{mediaUrls.length - maxItems}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!post) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-slate-500">Post not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const statusConfig = STATUS_CONFIG[post.status] || STATUS_CONFIG.draft;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="post-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/posts">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-slate-900">Post Details</h1>
                <Badge className={`${statusConfig.bg} ${statusConfig.text}`}>
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {calendar?.name} • Created {format(parseISO(post.created_at), 'MMM d, yyyy')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/posts/${postId}/edit`}>
              <Button variant="outline" data-testid="edit-post-btn">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </Link>
            <Button variant="outline" onClick={handleDuplicate}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button
              variant="outline"
              className="text-red-600 hover:bg-red-50"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-post-btn"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Status Progress */}
        <Card className="border-slate-100 mb-6">
          <CardContent className="py-6">
            <StatusProgressBar status={post.status} />
            <p className="text-center text-sm text-slate-500 mt-4">
              {statusConfig.description}
            </p>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Post Content Card */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Content</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap text-base leading-relaxed">
                  {post.content}
                </p>
                
                {post.hashtags?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {post.hashtags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="bg-emerald-50 text-emerald-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                {post.link && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <a
                      href={post.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-emerald-600 hover:underline break-all"
                    >
                      {post.link}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Media Attachments */}
            {post.media_urls?.length > 0 && (
              <Card className="border-slate-100" data-testid="media-attachments-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    Media Attachments
                    <Badge variant="secondary" className="ml-2">
                      {post.media_urls.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`grid gap-3 ${
                    post.media_urls.length === 1 ? 'grid-cols-1' :
                    post.media_urls.length === 2 ? 'grid-cols-2' :
                    'grid-cols-2 lg:grid-cols-3'
                  }`}>
                    {post.media_urls.map((url, index) => {
                      const isVideo = isVideoUrl(url);
                      const videoId = isVideo ? getVideoId(url) : null;
                      
                      return (
                        <div
                          key={`media-${url.slice(-12)}-${index}`}
                          className="relative group cursor-pointer rounded-lg overflow-hidden border border-slate-200 hover:border-emerald-400 transition-colors"
                          onClick={() => openMediaModal(url)}
                          data-testid={`media-item-${index}`}
                        >
                          {isVideo ? (
                            <div className="aspect-video bg-slate-100 relative">
                              <img
                                src={getVideoThumbnailUrl(url)}
                                alt={`Video ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                  <Play className="h-6 w-6 text-emerald-600 ml-1" />
                                </div>
                              </div>
                              <Badge className="absolute top-2 left-2 bg-black/60 text-white text-xs">
                                <Video className="h-3 w-3 mr-1" />
                                Video
                              </Badge>
                            </div>
                          ) : (
                            <div className="aspect-video bg-slate-100 relative">
                              <img
                                src={url}
                                alt={`Image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors opacity-0 group-hover:opacity-100">
                                <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                  <ZoomIn className="h-5 w-5 text-slate-600" />
                                </div>
                              </div>
                              <Badge className="absolute top-2 left-2 bg-black/60 text-white text-xs">
                                <Image className="h-3 w-3 mr-1" />
                                Image
                              </Badge>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Click on media to view in full size
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Platform Previews */}
            <Card className="border-slate-100" data-testid="platform-previews-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Monitor className="h-5 w-5" />
                  Platform Previews
                </CardTitle>
                <CardDescription>
                  See how this post will appear on each platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue={post.platforms[0] || 'facebook'}>
                  <TabsList className="w-full flex flex-wrap gap-1 h-auto p-1 mb-4">
                    {post.platforms.map((p) => {
                      const platform = PLATFORM_INFO[p];
                      if (!platform) return null;
                      return (
                        <TabsTrigger 
                          key={p} 
                          value={p} 
                          className="text-xs px-3 py-2 data-[state=active]:bg-white"
                          data-testid={`preview-tab-${p}`}
                        >
                          {platform.icon ? (
                            <platform.icon className="h-4 w-4 mr-1" style={{ color: platform.color }} />
                          ) : (
                            <span className="mr-1">{platform.emoji}</span>
                          )}
                          <span className="hidden sm:inline">{platform.name}</span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  <FacebookPreview post={post} renderMediaPreview={renderMediaPreview} />
                  <InstagramPreview post={post} renderMediaPreview={renderMediaPreview} />
                  <TwitterPreview post={post} renderMediaPreview={renderMediaPreview} />
                  <LinkedInPreview post={post} renderMediaPreview={renderMediaPreview} />
                  <TikTokPreview post={post} />
                  <YouTubePreview post={post} />
                  <PinterestPreview post={post} />
                  {post.platforms
                    .filter(p => !['facebook', 'instagram', 'twitter', 'linkedin', 'tiktok', 'youtube', 'pinterest'].includes(p))
                    .map(p => (
                      <GenericPreview
                        key={p}
                        platformId={p}
                        post={post}
                        platformInfo={PLATFORM_INFO[p]}
                        renderMediaPreview={renderMediaPreview}
                      />
                    ))}
                </Tabs>
              </CardContent>
            </Card>

            {/* Platforms */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Target Platforms</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {post.platforms.map((platformId) => {
                    const platform = PLATFORM_INFO[platformId];
                    if (!platform) return null;
                    
                    const publishData = post.platform_data?.[platformId];
                    const isPublished = publishData?.published;
                    const hasFailed = publishData && !publishData.published && publishData.error;
                    const isDemo = publishData?.method === 'demo';
                    
                    return (
                      <div
                        key={platformId}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border relative group ${
                          isPublished 
                            ? isDemo 
                              ? 'border-blue-200 bg-blue-50' 
                              : 'border-green-200 bg-green-50' 
                            : hasFailed 
                              ? 'border-red-200 bg-red-50' 
                              : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        {platform.icon ? (
                          <platform.icon className="h-5 w-5" style={{ color: platform.color }} />
                        ) : (
                          <span className="text-xl">{platform.emoji}</span>
                        )}
                        <span className="font-medium text-slate-700">{platform.name}</span>
                        {isPublished && !isDemo && (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        )}
                        {isPublished && isDemo && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">Demo</span>
                        )}
                        {hasFailed && (
                          <>
                            <XCircle className="h-4 w-4 text-red-600" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-red-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              {publishData.error}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Comments */}
            <CommentsSection
              comments={comments}
              newComment={newComment}
              onCommentChange={setNewComment}
              onAddComment={handleAddComment}
              submitting={submittingComment}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions Card */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Actions</CardTitle>
                <CardDescription>Advance this post through the workflow</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {getNextActionButton()}
                
                {post.status === 'pending_approval' && (
                  <Button
                    variant="outline"
                    className="w-full text-red-600 hover:bg-red-50"
                    onClick={() => setRejectDialogOpen(true)}
                    disabled={actionLoading}
                    data-testid="reject-btn"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                )}

                {post.status === 'draft' && (
                  <p className="text-xs text-slate-500 text-center">
                    Submit for approval to move to the next stage
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Schedule Info */}
            {post.scheduled_at && (
              <Card className="border-slate-100">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-900">
                      {format(parseISO(post.scheduled_at), 'MMM d')}
                    </p>
                    <p className="text-lg text-slate-600">
                      {format(parseISO(post.scheduled_at), 'h:mm a')}
                    </p>
                    <p className="text-sm text-slate-500 mt-2">
                      {format(parseISO(post.scheduled_at), 'EEEE, yyyy')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Published Info */}
            {post.status === 'published' && post.published_at && (
              <Card className="border-green-100 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    Published
                    {post.auto_published && (
                      <Badge variant="outline" className="ml-2 border-green-300 text-green-600 text-xs">
                        Auto
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-green-600">
                    {post.auto_published ? 'Auto-published' : 'Published'} on {format(parseISO(post.published_at), 'MMM d, yyyy \'at\' h:mm a')}
                  </p>
                  {post.auto_published && post.scheduled_at && (
                    <p className="text-xs text-green-500 mt-1">
                      Scheduled for {format(parseISO(post.scheduled_at), 'h:mm a')}
                    </p>
                  )}
                  {post.approved_by && (
                    <p className="text-xs text-green-500 mt-2">
                      Approved by team member
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Analytics Preview (for published posts) */}
            {post.status === 'published' && (
              <EngagementMetrics
                postAnalytics={postAnalytics}
                analyticsLoading={analyticsLoading}
                formatNumber={formatNumber}
              />
            )}

            {/* Activity Log */}
            <ActivityLog post={post} />
          </div>
        </div>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Post</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this post? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Reject Post
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-slate-600 mb-4">
                Please provide a reason for rejecting this post. The author will be notified and the post will return to draft status.
              </p>
              <Textarea
                placeholder="Reason for rejection (optional)..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="reject-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={actionLoading}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-reject-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Reject Post
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Media Preview Modal */}
        <Dialog open={mediaModalOpen} onOpenChange={(open) => !open && closeMediaModal()}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black/95">
            <DialogHeader className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-white">
                  {selectedMedia && isVideoUrl(selectedMedia) ? 'Video Preview' : 'Image Preview'}
                </DialogTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={closeMediaModal}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </DialogHeader>
            <div className="flex items-center justify-center min-h-[400px] max-h-[80vh]">
              {selectedMedia && isVideoUrl(selectedMedia) ? (
                <video
                  ref={videoRef}
                  src={selectedMedia}
                  controls
                  autoPlay
                  className="max-w-full max-h-[80vh] object-contain"
                  data-testid="media-modal-video"
                >
                  Your browser does not support video playback.
                </video>
              ) : selectedMedia ? (
                <img
                  src={selectedMedia}
                  alt="Media preview"
                  className="max-w-full max-h-[80vh] object-contain"
                  data-testid="media-modal-image"
                />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PostDetailPage;
